import React, { useState, useEffect, useRef } from "react";
import { Table, Button, Modal, Input } from "antd";
import styled from "styled-components";
import { toast } from "react-toastify";
import { DeleteIcon, EditIcon } from "../../../Utils/SvgIcons";
import DeleteModal from "../../../Components/modals/DeleteConfirm";
import { apiDelete, apiGet, apiPost, apiPut } from "../../../Utils/api";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const type = "DraggableRow";

const DraggableRow = ({
  index,
  moveRow,
  className,
  style,
  record,
  ...restProps
}) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: type,
    hover: (item) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <tr
      ref={ref}
      className={className}
      style={{ ...style, opacity: isDragging ? 0.5 : 1, cursor: "move" }}
      {...restProps}
    />
  );
};

const CategoriesTable = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch categories (sorted by position on the server)
  const fetchCategories = async (isNext = true) => {
    setLoading(true);
    try {
      const data = await apiGet("/api/categories");
      const categoryList = (data?.categories || []).map((cat) => ({
        key: cat.id,
        ...cat,
      }));
      setTotalCount(categoryList.length);
      setCategories(categoryList);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Error fetching categories.");
    } finally {
      setLoading(false);
    }
  };

  const moveRow = async (fromIndex, toIndex) => {
    const newData = [...categories];
    const [movedItem] = newData.splice(fromIndex, 1);
    newData.splice(toIndex, 0, movedItem);

    // Update position field
    newData.forEach((item, index) => {
      item.position = index + 1; // Reassign positions
    });

    setCategories([...newData]);

    // Persist new positions via the API
    await Promise.all(
      newData.map((item) =>
        apiPut(`/api/categories/${item.id}`, { position: item.position })
      )
    );
  };

  useEffect(() => {
    fetchCategories(false);
  }, []);

  // Handle adding or updating category in Firebase
  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      toast.error("Please enter a category name.");
      return;
    }

    setModalLoading(true);
    try {
      if (editingCategory) {
        // Update category
        await apiPut(`/api/categories/${editingCategory.id}`, {
          name: categoryName,
        });
        toast.success("Category updated successfully!");
      } else {
        // Add new category
        await apiPost("/api/categories", {
          name: categoryName,
          position: totalCount + 1,
        });
        toast.success("Category added successfully!");
      }
      setCategoryName("");
      setEditingCategory(null);
      setIsModalOpen(false);
      fetchCategories(false);
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error("Failed to save category.");
    } finally {
      setModalLoading(false);
    }
  };

  // Handle deleting category
  const handleDeleteCategory = async () => {
    try {
      const data = await apiGet(
        `/api/feeds?page=1&limit=1&category=${encodeURIComponent(
          editingCategory?.name || ""
        )}`
      );
      if ((data?.total || 0) > 0) {
        toast.error(
          `Cannot delete this category. It is assigned to one or more feeds.`,
          { theme: "colored" }
        );
      } else {
        setDeleteLoading(true);
        await apiDelete(`/api/categories/${editingCategory.id}`);
        toast.success("Category deleted successfully!");
        fetchCategories(false);
        setDeleteModalVisible(false);
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Failed to delete category.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle opening modal for editing
  const openEditModal = (record) => {
    setEditingCategory(record);
    setCategoryName(record.name);
    setIsModalOpen(true);
  };

  // Table Columns
  const columns = [
    {
      title: "S.No.",
      key: "index",
      render: (_, __, index) => index + 1 + ".",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <ActionBox>
          <button className="edit" onClick={() => openEditModal(record)}>
            <EditIcon />
          </button>

          <button
            className="delete"
            onClick={() => {
              setEditingCategory(record);
              setDeleteModalVisible(true);
            }}
          >
            <DeleteIcon />
          </button>
        </ActionBox>
      ),
    },
  ];

  return (
    <div style={{ margin: "20px 0px" }}>
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h2 style={{ textAlign: "start", fontFamily: "Poppins" }}>
          Categories
        </h2>
        <CreateButton
          onClick={() => {
            setEditingCategory(null);
            setCategoryName("");
            setIsModalOpen(true);
          }}
        >
          + Create
        </CreateButton>
      </div>
      <DndProvider backend={HTML5Backend}>
        <Table
          columns={columns}
          dataSource={categories}
          loading={loading}
          pagination={false}
          rowKey={(record) => record.key} // Ensure stable row keys
          components={{
            body: {
              row: (props) => {
                const index = categories.findIndex(
                  (item) => item.key === props["data-row-key"]
                );
                return (
                  <DraggableRow {...props} index={index} moveRow={moveRow} />
                );
              },
            },
          }}
        />
      </DndProvider>

      {/* Create/Edit Category Modal */}
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setIsModalOpen(false)}
            disabled={modalLoading}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveCategory}
            loading={modalLoading} // Show loading state here
          >
            {editingCategory ? "Update" : "Create"}
          </Button>,
        ]}
      >
        <ModalStyle>
          <h2>{editingCategory ? "Edit Category" : "Create Category"}</h2>
          <Input
            placeholder="Enter category name"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            disabled={modalLoading} // Disable input while loading
          />
        </ModalStyle>
      </Modal>

      {deleteModalVisible && (
        <DeleteModal
          visible={deleteModalVisible}
          onConfirm={handleDeleteCategory}
          onCancel={() => setDeleteModalVisible(false)}
          loading={deleteLoading}
          itemName="this category"
        />
      )}
    </div>
  );
};

export default CategoriesTable;

const ModalStyle = styled.div`
  width: 100%;

  h2 {
    font-family: "Poppins", sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 30px;
    color: #000000;
  }
`;

export const CreateButton = styled.button`
  background: #28a3a9;
  border-radius: 6px;
  border: none;
  color: #ffffff;
  padding: 6px 20px;
  font-size: 14px;
  cursor: pointer;
  font-weight: 600;
  font-family: "Poppins", sans-serif;
`;

const ActionBox = styled.div`
  display: flex;
  gap: 10px;

  .edit {
    background: #28a3a9;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-style: none;
  }
  .delete {
    background: #fb4a49;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-style: none;
  }
`;
